import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { assertReadMethod } from "./http-read-provider";
import { ELYSIUM } from "../config/elysium";
import { configuredElysiumMintProvider, configuredElysiumRpcUrl } from "./elysium-rpc";
import type { ReferralAuthorization } from "../features/referrals/api/referral-schemas";
import type { ReferralMintEvidence } from "../features/referrals/contracts/referral-mint";
import { observeElysium } from "./elysium-readiness";
import { responseNow } from "../api/response-time";

type Head = { number: string; hash: string; timestamp: string };
type Session = { provider: Eip1193Provider; sampledAt(): number; refresh(timeoutMs?: number): Promise<void> };
const sessions = new WeakMap<Eip1193Provider, Session>();

/** One operation, immutable block-hash reads only. Live simulations and canonicality
 * reads never use the cache. A refreshed head establishes a new final-check snapshot. */
export async function createMintReadSession(upstream: Eip1193Provider, guard: () => void,
  options: RpcReadOptions = {}): Promise<Session> {
  let head: Head;
  let sampledAt = 0;
  const cache = new Map<string, Promise<unknown>>();
  const check = () => { options.signal?.throwIfAborted(); guard(); };
  const refresh = async (timeoutMs?: number) => {
    check();
    const started = performance.now();
    const readOptions = { ...options, ...(timeoutMs === undefined ? {} : { timeoutMs: Math.min(timeoutMs, options.timeoutMs ?? 15_000) }) };
    const [chain, raw] = await Promise.all([
      readRpc(upstream, { method: "eth_chainId" }, readOptions),
      readRpc(upstream, { method: "eth_getBlockByNumber", params: ["latest", false] }, readOptions),
    ]);
    check();
    if (chain !== `0x${ELYSIUM.chainId.toString(16)}`) throw new Error("ELYSIUM_CHAIN_MISMATCH");
    const value = raw as Partial<Head> | null;
    if (!value || typeof value.number !== "string" || !/^0x[0-9a-f]+$/i.test(value.number) ||
      typeof value.hash !== "string" || !/^0x[0-9a-f]{64}$/i.test(value.hash) || BigInt(value.hash) === 0n ||
      typeof value.timestamp !== "string" || !/^0x[0-9a-f]+$/i.test(value.timestamp)) throw new Error("ELYSIUM_HEAD_INVALID");
    // The final phase must perform fresh reads even if the RPC reports the same
    // head, so inconsistent data or an implementation change cannot hide in cache.
    cache.clear();
    head = { number: value.number, hash: value.hash, timestamp: value.timestamp }; sampledAt = started;
  };
  await refresh();
  const provider: Eip1193Provider = { request: async args => {
    check(); assertReadMethod(args.method, "MINT_SNAPSHOT");
    const params = Array.isArray(args.params) ? [...args.params] : [];
    if (args.method === "eth_getBlockByNumber" && params[0] === "latest" && params[1] === false) return { ...head };
    const index = args.method === "eth_getStorageAt" ? 2 : ["eth_call", "eth_getCode"].includes(args.method) ? 1 : -1;
    // Sender-dependent simulations always execute live, with the exact original call.
    const simulation = args.method === "eth_call" && params[0] && typeof params[0] === "object" && "from" in params[0];
    if (index < 0 || simulation) {
      const value = await upstream.request(args); check(); return value;
    }
    const reference = params[index];
    if (reference === "latest" || typeof reference === "string" && /^0x[0-9a-f]+$/i.test(reference) && BigInt(reference) === BigInt(head.number)) {
      params[index] = { blockHash: head.hash, requireCanonical: true };
    }
    const pin = params[index];
    if (!pin || typeof pin !== "object" || !("blockHash" in pin) || !("requireCanonical" in pin) || pin.requireCanonical !== true) {
      const value = await upstream.request(args); check(); return value;
    }
    const key = JSON.stringify([args.method, params], (_key, value: unknown) =>
      typeof value === "string" && value.startsWith("0x") ? value.toLowerCase() : value);
    let result = cache.get(key);
    if (!result) {
      result = upstream.request({ method: args.method, params });
      cache.set(key, result);
      void result.catch(() => { if (cache.get(key) === result) cache.delete(key); });
    }
    const value = await result; check(); return value;
  } };
  const session = { provider, sampledAt: () => sampledAt, refresh };
  sessions.set(provider, session);
  return session;
}

export function mintSnapshotSampledAt(provider: Eip1193Provider): number | undefined {
  return sessions.get(provider)?.sampledAt();
}
export async function refreshMintSnapshot(provider: Eip1193Provider, timeoutMs?: number): Promise<void> {
  await sessions.get(provider)?.refresh(timeoutMs);
}

type Warm = { evidence: ReferralMintEvidence; url: string; started: number; check(): void; session: Promise<Session> };
const reviews = new WeakMap<ReferralAuthorization, Warm>();

/** Speculative reads only: the sending path still runs all validations, a live
 * simulation, fresh final checks and the original wallet/context guards. */
export function warmReferralMintReads(result: ReferralAuthorization, evidence: ReferralMintEvidence,
  guard: () => void, signal: AbortSignal): () => void {
  if (result.chainId !== ELYSIUM.chainId) return () => {};
  const abort = new AbortController();
  const cancel = () => abort.abort();
  signal.addEventListener("abort", cancel, { once: true });
  if (signal.aborted) cancel();
  const url = configuredElysiumRpcUrl()?.href;
  const provider = configuredElysiumMintProvider(abort.signal);
  if (!url || !provider) { signal.removeEventListener("abort", cancel); return cancel; }
  const check = () => {
    abort.signal.throwIfAborted(); guard();
    if (configuredElysiumRpcUrl()?.href !== url) throw new Error("Mint RPC changed");
  };
  const options = { signal: abort.signal };
  const session = createMintReadSession(provider, check, options);
  const warm = { evidence, url, started: performance.now(), check, session };
  reviews.set(result, warm);
  void session.then(async snapshot => {
    await Promise.all([
      evidence.verifyDeployment(snapshot.provider, check, options),
      observeElysium(snapshot.provider, check, options, () => {
        const now = responseNow(result);
        if (now === null) throw new Error("MINT_SERVER_TIME_UNAVAILABLE");
        return now;
      }),
    ]);
  }).catch(() => { if (reviews.get(result) === warm) reviews.delete(result); });
  return () => { cancel(); signal.removeEventListener("abort", cancel); if (reviews.get(result) === warm) reviews.delete(result); };
}

export async function takeReferralMintReads(result: ReferralAuthorization, evidence: ReferralMintEvidence): Promise<Session | null> {
  const warm = reviews.get(result); reviews.delete(result);
  if (!warm || warm.evidence !== evidence || warm.url !== configuredElysiumRpcUrl()?.href || performance.now() - warm.started > 10_000) return null;
  try {
    warm.check();
    const snapshot = await warm.session;
    warm.check();
    return performance.now() - snapshot.sampledAt() <= 10_000 ? snapshot : null;
  } catch { return null; }
}
